import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus, Search, Globe, BarChart3, Trash2, TrendingUp, TrendingDown,
  Users, Link2, RefreshCw, ExternalLink, Sparkles,
  Target, Zap, Loader2, Globe2, FileSearch, Activity,
  CheckCircle, AlertCircle, Clock, FileText, ChevronRight,
  AlertTriangle, Info, GitCompare, Bell, Rss, Eye, History,
  Download, Shield, Smartphone, Code, Image, XCircle, ChevronDown, Pencil, Megaphone
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import UsageBadge from '../../components/ui/UsageBadge';
import { competitorService } from '../../services/competitorService';
import { Competitor } from '../../types';
import { jsPDF } from 'jspdf';

// Tab types
type TabType = 'overview' | 'benchmark' | 'audit';

interface SiteData {
  domain: string;
  organicTraffic: number;
  paidTraffic: number;
  domainAuthority: number;
  backlinks: number;
  referringDomains: number;
  organicKeywords: number;
  trafficValue: number;
  trafficHistory: { date: string; organic: number; paid: number }[];
  topPages: { url: string; traffic: number; keywords: number; share: number }[];
  topKeywords: { keyword: string; position: number; volume: number; traffic: number }[];
  trafficByCountry: { country: string; traffic: number; share: number }[];
  performance?: { score: number; seoScore: number; lcp: string; cls: string };
}

interface AuditData {
  id: string;
  url: string;
  score: number;
  issues: { critical: number; warnings: number; notices: number };
  createdAt: string;
}

interface AuditIssue {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'warning' | 'info' | 'passed';
  category: string;
  recommendation: string;
  affectedUrls?: string[];
}

interface AuditCategory {
  name: string;
  icon?: React.ReactNode;
  score: number;
  issues: AuditIssue[];
  expanded: boolean;
}

interface CoreWebVital {
  value: number;
  displayValue: string;
  score: number;
}

interface FullAuditResult {
  url: string;
  overallScore: number;
  categories: AuditCategory[];
  totalIssues: { critical: number; warning: number; info: number; passed: number };
  auditedAt: string;
  pageSpeed: { mobile: number; desktop: number };
  scores?: {
    mobile: { performance: number; accessibility: number; bestPractices: number; seo: number };
    desktop: { performance: number; accessibility: number; bestPractices: number; seo: number };
  };
  coreWebVitals?: {
    lcp: CoreWebVital;
    fid: CoreWebVital;
    cls: CoreWebVital;
    fcp: CoreWebVital;
    ttfb: CoreWebVital;
    speedIndex: CoreWebVital;
    tbt: CoreWebVital;
    tti: CoreWebVital;
  };
  screenshot?: {
    final: string | null;
    timing: number | null;
    thumbnails: Array<{ timing: number; timestamp: number; data: string }>;
  };
  lighthouseVersion?: string;
  mobile?: {
    overallScore: number;
    categories: AuditCategory[];
    totalIssues: { critical: number; warning: number; info: number; passed: number };
    coreWebVitals?: any;
    screenshot?: any;
  };
  desktop?: {
    overallScore: number;
    categories: AuditCategory[];
    totalIssues: { critical: number; warning: number; info: number; passed: number };
    coreWebVitals?: any;
    screenshot?: any;
  };
  seoAnalysis?: {
    score: number;
    loadTime: number;
    contentLength: number;
    meta: {
      title: string;
      titleLength: number;
      description: string;
      descriptionLength: number;
      canonical: string;
      ogTitle: string;
      ogDescription: string;
    };
    headings: {
      h1: string[];
      h2: string[];
      h3: string[];
      h1Count: number;
      h2Count: number;
      h3Count: number;
      h4Count: number;
    };
    links: {
      internalCount: number;
      externalCount: number;
    };
    images: {
      total: number;
      withAlt: number;
      withoutAlt: number;
    };
    content: {
      wordCount: number;
      paragraphs: number;
      textToHtmlRatio: number;
    };
    technical: {
      hasViewport: boolean;
      hasCharset: boolean;
      hasLangAttribute: boolean;
      usesHttps: boolean;
      hasStructuredData: boolean;
      hasFavicon: boolean;
    };
    issues: Array<{ type: string; code: string; message: string }>;
  };
}

interface AuditHistoryItem {
  id: string;
  url: string;
  status: string;
  scores: {
    overall: number;
    seo: number;
    performance: number;
    accessibility: number;
    bestPractices: number;
  };
  total_issues: number;
  critical_issues: number;
  warnings: number;
  info_issues: number;
  page_speed_mobile: number;
  page_speed_desktop: number;
  lighthouse_version: string;
  created_at: string;
}

interface ContentChange {
  id: string;
  competitorId: string;
  competitorName: string;
  domain: string;
  changeType: 'new_page' | 'content_update' | 'page_removed' | 'new_backlink';
  url: string;
  title?: string;
  detectedAt: string;
  details?: string;
}

interface Region {
  code: string;
  name: string;
  flag: string;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const CompetitorsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active tab
  const [activeTab, setActiveTab] = useState<TabType>(
    (searchParams.get('tab') as TabType) || 'overview'
  );

  // States
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', domain: '', industry: '', country: 'BR' });
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<Competitor | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Explorer states
  const [explorerDomain, setExplorerDomain] = useState('');
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [siteData, setSiteData] = useState<SiteData | null>(null);

  // Full Audit states
  const [auditUrl, setAuditUrl] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);
  const [fullAuditResult, setFullAuditResult] = useState<FullAuditResult | null>(null);
  const [auditHistory, setAuditHistory] = useState<AuditHistoryItem[]>([]);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [loadingAuditId, setLoadingAuditId] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Monitoring states
  const [contentChanges, setContentChanges] = useState<ContentChange[]>([]);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoredDomains, setMonitoredDomains] = useState<string[]>([]);
  const [newMonitorDomain, setNewMonitorDomain] = useState('');

  // Benchmark states
  const [selectedForBenchmark, setSelectedForBenchmark] = useState<string[]>([]);
  const [benchmarkData, setBenchmarkData] = useState<any>(null);
  const [mySiteDomain, setMySiteDomain] = useState('');
  const [includeMySite, setIncludeMySite] = useState(false);
  const [mySiteData, setMySiteData] = useState<SiteData | null>(null);
  const [loadingMySite, setLoadingMySite] = useState(false);

  // Tab config
  const tabs = [
    { id: 'overview' as TabType, label: t('competitors.myCompetitorsTab', 'Meus concorrentes'), icon: Users },
    { id: 'benchmark' as TabType, label: t('competitors.compareTab', 'Comparar'), icon: GitCompare },
    { id: 'audit' as TabType, label: t('competitors.siteAudit', 'Auditoria SEO'), icon: FileSearch },
  ];

  useEffect(() => {
    loadCompetitors();
  }, []);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
    if (activeTab === 'audit' && auditHistory.length === 0) {
      loadAuditHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    if (showAuditHistory && auditHistory.length === 0) {
      loadAuditHistory();
    }
  }, [showAuditHistory]);

  const loadCompetitors = async () => {
    setIsLoading(true);
    try {
      const response = await competitorService.getCompetitors({ search: search || undefined });
      if (response.success && response.data) {
        setCompetitors(response.data);
      } else {
        setCompetitors([]);
      }
    } catch (error) {
      console.error('Error loading competitors:', error);
      setCompetitors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAuditHistory = useCallback(async (page: number = 1) => {
    setHistoryLoading(true);
    try {
      const response = await competitorService.getAuditHistory({ page, limit: 10 });
      if (response.success) {
        setAuditHistory(response.data);
        setHistoryTotal(response.pagination?.total || 0);
        setHistoryTotalPages(response.pagination?.totalPages || 0);
        setHistoryPage(page);
      }
    } catch (error) {
      console.error('Error loading audit history:', error);
      toast.error(t('competitors.historyError', 'Error loading history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [t]);

  const loadAuditFromHistory = useCallback(async (auditId: string) => {
    setLoadingAuditId(auditId);
    try {
      const response = await competitorService.getAuditById(auditId);
      if (response.success && response.data) {
        const result = response.data;
        const categoryIcons: Record<string, React.ReactNode> = {
          'SEO': <FileText className="w-5 h-5" />,
          'SEO On-Page': <FileText className="w-5 h-5" />,
          'Performance': <Zap className="w-5 h-5" />,
          'Links': <Link2 className="w-5 h-5" />,
          'Security': <Shield className="w-5 h-5" />,
          'Seguranca': <Shield className="w-5 h-5" />,
          'Accessibility': <Smartphone className="w-5 h-5" />,
          'Mobile': <Smartphone className="w-5 h-5" />,
          'Tecnico': <Code className="w-5 h-5" />
        };
        const addIconsToCategories = (categories: any[]) => categories?.map((cat: any) => ({
          ...cat,
          icon: categoryIcons[cat.name] || <FileText className="w-5 h-5" />,
          expanded: false
        })) || [];
        const processedResult = {
          ...result,
          categories: addIconsToCategories(result.categories || []),
          mobile: result.mobile ? { ...result.mobile, categories: addIconsToCategories(result.mobile.categories || []) } : undefined,
          desktop: result.desktop ? { ...result.desktop, categories: addIconsToCategories(result.desktop.categories || []) } : undefined
        };
        setFullAuditResult(processedResult);
        setAuditUrl(result.url || '');
        setShowAuditHistory(false);
        const criticalCategories = new Set<string>(
          (processedResult.categories || []).filter((cat: AuditCategory) => cat.issues?.some(i => i.severity === 'critical')).map((cat: AuditCategory) => cat.name)
        );
        setExpandedCategories(criticalCategories);
      }
    } catch (error) {
      console.error('Error loading audit:', error);
      toast.error(t('competitors.loadError', 'Error loading audit'));
    } finally {
      setLoadingAuditId(null);
    }
  }, [t]);

  const deleteAuditFromHistory = useCallback(async (auditId: string) => {
    if (!confirm(t('competitors.deleteAuditConfirm', 'Tem certeza que deseja excluir esta auditoria?'))) return;
    try {
      const response = await competitorService.deleteAudit(auditId);
      if (response.success) {
        toast.success(t('competitors.deleteAuditSuccess', 'Audit deleted successfully'));
        loadAuditHistory(historyPage);
      }
    } catch (error) {
      console.error('Error deleting audit:', error);
      toast.error(t('competitors.deleteAuditError', 'Error deleting audit'));
    }
  }, [t, historyPage, loadAuditHistory]);

  const handleExportPdf = async () => {
    if (!fullAuditResult) return;

    setIsExportingPdf(true);
    toast.loading(t('competitors.generatingPDF', 'Generating PDF...'), { id: 'pdf-export' });

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 15;
      let yPos = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Site Audit Report', margin, yPos);
      yPos += 10;

      // URL
      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.text(auditUrl, margin, yPos);
      yPos += 8;

      // Date
      pdf.setFontSize(10);
      pdf.text(`Data: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 15;

      // Overall Score
      pdf.setFontSize(16);
      pdf.setTextColor(31, 41, 55);
      pdf.text(`Score Geral: ${fullAuditResult.overallScore}/100`, margin, yPos);
      yPos += 12;

      // Issues Summary
      pdf.setFontSize(14);
      pdf.text('Resumo de Problemas', margin, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`• Criticos: ${fullAuditResult.totalIssues?.critical || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• Avisos: ${fullAuditResult.totalIssues?.warning || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• Informacoes: ${fullAuditResult.totalIssues?.info || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• Passou: ${fullAuditResult.totalIssues?.passed || 0}`, margin + 5, yPos);
      yPos += 12;

      // Core Web Vitals
      if (fullAuditResult.coreWebVitals) {
        pdf.setFontSize(14);
        pdf.setTextColor(31, 41, 55);
        pdf.text('Core Web Vitals', margin, yPos);
        yPos += 8;

        pdf.setFontSize(11);
        pdf.setTextColor(75, 85, 99);
        if (fullAuditResult.coreWebVitals.lcp) {
          pdf.text(`• LCP: ${fullAuditResult.coreWebVitals.lcp.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        if (fullAuditResult.coreWebVitals.cls) {
          pdf.text(`• CLS: ${fullAuditResult.coreWebVitals.cls.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        if (fullAuditResult.coreWebVitals.ttfb) {
          pdf.text(`• TTFB: ${fullAuditResult.coreWebVitals.ttfb.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        yPos += 8;
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text('Generated by DevSkin Marketing Platform', margin, 290);

      // Save PDF
      const fileName = `site-audit-${auditUrl.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      toast.success(t('competitors.exportSuccess', 'PDF exported successfully!'), { id: 'pdf-export' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('competitors.exportError', 'Error exporting PDF'), { id: 'pdf-export' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Load monitoring data (mock for now)
  const loadMonitoringData = useCallback(async () => {
    setMonitoringLoading(true);
    try {
      // Simulate loading content changes from competitors
      const mockChanges: ContentChange[] = competitors.slice(0, 3).flatMap(c => ([
        {
          id: `${c.id}-1`,
          competitorId: c.id,
          competitorName: c.name,
          domain: c.domain,
          changeType: 'new_page' as const,
          url: `https://${c.domain}/blog/novo-artigo`,
          title: 'Nova pagina detectada',
          detectedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          details: 'Nova pagina de blog publicada'
        },
        {
          id: `${c.id}-2`,
          competitorId: c.id,
          competitorName: c.name,
          domain: c.domain,
          changeType: 'content_update' as const,
          url: `https://${c.domain}/`,
          title: 'Homepage atualizada',
          detectedAt: new Date(Date.now() - Math.random() * 3 * 24 * 60 * 60 * 1000).toISOString(),
          details: 'Mudancas no conteudo principal'
        }
      ]));
      setContentChanges(mockChanges.sort((a, b) => new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()));
      setMonitoredDomains(competitors.map(c => c.domain));
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setMonitoringLoading(false);
    }
  }, [competitors]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadCompetitors();
  };

  const loadRegions = async () => {
    try {
      const response = await competitorService.getAdsRegions();
      if (response.success && response.data) {
        setRegions(response.data);
      }
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  };

  const handleOpenAddModal = () => {
    setIsModalOpen(true);
    if (regions.length === 0) {
      loadRegions();
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name.trim() || !newCompetitor.domain.trim()) return;
    setIsAdding(true);
    try {
      const response = await competitorService.createCompetitor(newCompetitor);
      setNewCompetitor({ name: '', domain: '', industry: '', country: 'BR' });
      setIsModalOpen(false);

      // Redirect to detail page and auto-start analysis
      if (response.success && response.data?.id) {
        toast.success(t('competitors.addSuccessAnalyzing', 'Competitor added! Starting analysis...'));
        navigate(`/competitors/${response.data.id}?analyze=true`);
      } else {
        toast.success(t('competitors.addSuccess', 'Competitor added successfully'));
        loadCompetitors();
      }
    } catch (error: any) {
      console.error('Error adding competitor:', error);
      toast.error(t('competitors.addError', 'Error adding competitor'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('competitors.deleteConfirm', 'Tem certeza que deseja remover este concorrente?'))) return;
    try {
      await competitorService.deleteCompetitor(id);
      toast.success(t('competitors.deleteSuccess', 'Competitor removed'));
      loadCompetitors();
    } catch (error) {
      console.error('Error deleting competitor:', error);
      toast.error(t('competitors.deleteError', 'Error removing competitor'));
    }
    setOpenMenu(null);
  };

  const handleOpenEditModal = (competitor: Competitor) => {
    setEditingCompetitor({ ...competitor });
    setIsEditModalOpen(true);
    if (regions.length === 0) {
      loadRegions();
    }
  };

  const handleSaveEdit = async () => {
    if (!editingCompetitor) return;
    setIsSaving(true);
    try {
      await competitorService.updateCompetitor(editingCompetitor.id, {
        name: editingCompetitor.name,
        industry: editingCompetitor.industry,
        country: editingCompetitor.country
      });
      toast.success(t('competitors.editSuccess', 'Competitor updated successfully'));
      setIsEditModalOpen(false);
      setEditingCompetitor(null);
      loadCompetitors();
    } catch (error) {
      console.error('Error updating competitor:', error);
      toast.error(t('competitors.editError', 'Error updating competitor'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnalyze = async (id: string) => {
    // Navega diretamente para a página de detalhes com parâmetro de análise
    navigate(`/competitors/${id}?analyze=true`);
  };

  const handleExplore = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!explorerDomain.trim()) return;

    setExplorerLoading(true);
    try {
      const response = await competitorService.exploreSite(explorerDomain.trim());
      if (response.success && response.data) {
        setSiteData(response.data);
      } else {
        toast.error(t('competitors.noDataFound', 'Data not found for this domain'));
        setSiteData(null);
      }
    } catch (error) {
      console.error('Error exploring site:', error);
      toast.error(t('errors.generic', 'Error fetching data'));
      setSiteData(null);
    } finally {
      setExplorerLoading(false);
    }
  };

  const handleRunAudit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!auditUrl.trim()) return;

    setAuditLoading(true);
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      const response = await competitorService.runSiteAudit(auditUrl.trim(), true, true);

      let result = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('siteAudit.runningInBackground', 'Running audit in background...'), { id: 'audit' });

        const jobResult = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          180000 // 3 minute timeout
        );

        toast.dismiss('audit');
        result = jobResult;
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result) {
        // Add icons to categories
        const categoryIcons: Record<string, React.ReactNode> = {
          'SEO': <FileText className="w-5 h-5" />,
          'SEO On-Page': <FileText className="w-5 h-5" />,
          'Performance': <Zap className="w-5 h-5" />,
          'Links': <Link2 className="w-5 h-5" />,
          'Security': <Shield className="w-5 h-5" />,
          'Seguranca': <Shield className="w-5 h-5" />,
          'Accessibility': <Smartphone className="w-5 h-5" />,
          'Mobile': <Smartphone className="w-5 h-5" />,
          'Tecnico': <Code className="w-5 h-5" />
        };

        const addIconsToCategories = (categories: any[]) => categories?.map((cat: any) => ({
          ...cat,
          icon: categoryIcons[cat.name] || <FileText className="w-5 h-5" />,
          expanded: false
        })) || [];

        const processedResult: FullAuditResult = {
          ...result,
          categories: addIconsToCategories(result.categories || []),
          mobile: result.mobile ? {
            ...result.mobile,
            categories: addIconsToCategories(result.mobile.categories || [])
          } : undefined,
          desktop: result.desktop ? {
            ...result.desktop,
            categories: addIconsToCategories(result.desktop.categories || [])
          } : undefined
        };

        setFullAuditResult(processedResult);

        // Expand categories with critical issues by default
        const criticalCategories = new Set<string>(
          (processedResult.categories || []).filter(cat => cat.issues?.some(i => i.severity === 'critical')).map(cat => cat.name)
        );
        setExpandedCategories(criticalCategories);

        loadAuditHistory();
        toast.success(t('competitors.auditCompleted', 'Audit completed!'));
      }
    } catch (error) {
      console.error('Error running audit:', error);
      toast.dismiss('audit');
      toast.error(t('errors.generic', 'Error running audit'));
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleBenchmarkSelection = (id: string) => {
    setSelectedForBenchmark(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= 4) {
        toast.error(t('competitors.maxBenchmark', 'Maximum of 4 competitors to compare'));
        return prev;
      }
      return [...prev, id];
    });
  };

  const loadMySiteData = async () => {
    if (!mySiteDomain.trim()) return;
    setLoadingMySite(true);
    try {
      const response = await competitorService.exploreSite(mySiteDomain.trim());
      if (response.success && response.data) {
        setMySiteData(response.data);
        setIncludeMySite(true);
        toast.success(t('competitors.mySiteLoaded', 'Your site data loaded!'));
      }
    } catch (error) {
      console.error('Error loading my site:', error);
      toast.error(t('errors.generic', 'Error loading site data'));
    } finally {
      setLoadingMySite(false);
    }
  };

  const runBenchmark = async () => {
    const minRequired = includeMySite && mySiteData ? 1 : 2;
    if (selectedForBenchmark.length < minRequired) {
      toast.error(t('competitors.minBenchmark', 'Select at least 1 competitor'));
      return;
    }

    // Build benchmark data from selected competitors
    const selected = competitors.filter(c => selectedForBenchmark.includes(c.id));

    // Create "My Site" as a competitor-like object if included
    const mySiteCompetitor = includeMySite && mySiteData ? {
      id: 'my-site',
      name: t('competitors.mySite', 'Meu Site') + ` (${mySiteData.domain})`,
      domain: mySiteData.domain,
      domain_authority: mySiteData.domainAuthority,
      organic_traffic: mySiteData.organicTraffic,
      backlinks_count: mySiteData.backlinks,
      isMysite: true
    } : null;

    const allCompetitors = mySiteCompetitor
      ? [mySiteCompetitor, ...selected]
      : selected;

    const radarData = [
      { metric: 'DA', fullMark: 100 },
      { metric: t('competitors.traffic', 'Trafego'), fullMark: 100 },
      { metric: 'Backlinks', fullMark: 100 },
      { metric: 'Keywords', fullMark: 100 },
      { metric: 'Performance', fullMark: 100 },
    ];

    // Normalize data for radar chart
    const maxDA = Math.max(...allCompetitors.map(c => c.domain_authority || 1));
    const maxTraffic = Math.max(...allCompetitors.map(c => c.organic_traffic || 1));
    const maxBacklinks = Math.max(...allCompetitors.map(c => c.backlinks_count || 1));

    const normalizedData = radarData.map(item => {
      const row: any = { metric: item.metric };
      allCompetitors.forEach(c => {
        if (item.metric === 'DA') {
          row[c.name] = Math.round(((c.domain_authority || 0) / maxDA) * 100);
        } else if (item.metric.includes('raf') || item.metric.includes('Traf')) {
          row[c.name] = Math.round(((c.organic_traffic || 0) / maxTraffic) * 100);
        } else if (item.metric === 'Backlinks') {
          row[c.name] = Math.round(((c.backlinks_count || 0) / maxBacklinks) * 100);
        } else {
          row[c.name] = Math.round(50 + Math.random() * 50);
        }
      });
      return row;
    });

    setBenchmarkData({
      competitors: allCompetitors,
      radarData: normalizedData,
      mySite: mySiteCompetitor,
    });
  };

  // Calculate summary stats
  const stats = {
    totalCompetitors: competitors.length,
    avgDomainAuthority: Math.round(competitors.reduce((sum, c) => sum + (c.domain_authority || 0), 0) / (competitors.length || 1)),
    totalOrganicTraffic: competitors.reduce((sum, c) => sum + (c.organic_traffic || 0), 0),
    totalBacklinks: competitors.reduce((sum, c) => sum + (c.backlinks_count || 0), 0)
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getDAColor = (da: number) => {
    if (da >= 70) return 'text-green-600';
    if (da >= 50) return 'text-yellow-600';
    if (da >= 30) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="danger">{t('competitors.critical', 'Critico')}</Badge>;
      case 'warning':
        return <Badge variant="warning">{t('competitors.warning', 'Aviso')}</Badge>;
      case 'info':
        return <Badge variant="primary">{t('competitors.info', 'Info')}</Badge>;
      case 'passed':
        return <Badge variant="success">{t('competitors.passed', 'OK')}</Badge>;
      default:
        return null;
    }
  };

  // Computed data based on selected device (mobile/desktop toggle)
  const currentAuditData = useMemo(() => {
    if (!fullAuditResult) return null;
    const deviceData = selectedDevice === 'mobile' ? fullAuditResult.mobile : fullAuditResult.desktop;
    if (!deviceData) {
      // Fallback: use legacy data from fullAuditResult
      return {
        overallScore: fullAuditResult.overallScore,
        categories: fullAuditResult.categories,
        totalIssues: fullAuditResult.totalIssues,
        coreWebVitals: fullAuditResult.coreWebVitals,
        screenshot: fullAuditResult.screenshot
      };
    }
    return deviceData;
  }, [fullAuditResult, selectedDevice]);

  const handleExportPDF = async () => {
    if (!fullAuditResult || !currentAuditData) return;

    setIsExportingPdf(true);
    toast.loading(t('competitors.generatingPDF', 'Generating PDF...'), { id: 'pdf-export' });

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 15;
      let yPos = 20;

      pdf.setFontSize(20);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Site Audit Report', margin, yPos);
      yPos += 10;

      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.text(auditUrl, margin, yPos);
      yPos += 8;

      pdf.setFontSize(10);
      pdf.text(`${t('common.date', 'Data')}: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 15;

      pdf.setFontSize(16);
      pdf.setTextColor(31, 41, 55);
      pdf.text(`${t('competitors.overallScore', 'Pontuacao Geral')}: ${currentAuditData.overallScore}/100`, margin, yPos);
      yPos += 12;

      pdf.setFontSize(14);
      pdf.text(t('competitors.issuesSummary', 'Resumo de Problemas'), margin, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`• ${t('competitors.critical', 'Critico')}: ${currentAuditData.totalIssues?.critical || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• ${t('competitors.warning', 'Aviso')}: ${currentAuditData.totalIssues?.warning || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• ${t('competitors.info', 'Info')}: ${currentAuditData.totalIssues?.info || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• ${t('competitors.passed', 'OK')}: ${currentAuditData.totalIssues?.passed || 0}`, margin + 5, yPos);
      yPos += 12;

      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text('Generated by DevSkin Marketing Platform', margin, 290);

      const fileName = `site-audit-${auditUrl.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      toast.success(t('competitors.exportSuccess', 'PDF exported successfully!'), { id: 'pdf-export' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('competitors.exportError', 'Error exporting PDF'), { id: 'pdf-export' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const getChangeTypeIcon = (changeType: ContentChange['changeType']) => {
    switch (changeType) {
      case 'new_page':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'content_update':
        return <RefreshCw className="w-5 h-5 text-blue-500" />;
      case 'page_removed':
        return <Trash2 className="w-5 h-5 text-red-500" />;
      case 'new_backlink':
        return <Link2 className="w-5 h-5 text-purple-500" />;
      default:
        return <Globe className="w-5 h-5 text-gray-500" />;
    }
  };

  const getChangeTypeBadge = (changeType: ContentChange['changeType']) => {
    switch (changeType) {
      case 'new_page':
        return <Badge variant="success">{t('competitors.newPage', 'Nova Pagina')}</Badge>;
      case 'content_update':
        return <Badge variant="primary">{t('competitors.contentUpdate', 'Atualizacao')}</Badge>;
      case 'page_removed':
        return <Badge variant="danger">{t('competitors.pageRemoved', 'Removida')}</Badge>;
      case 'new_backlink':
        return <Badge variant="warning">{t('competitors.newBacklink', 'Novo Backlink')}</Badge>;
      default:
        return <Badge variant="gray">{t('common.unknown', 'Desconhecido')}</Badge>;
    }
  };

  // A auditoria agora tem aba própria (/competitors/site-audit); links antigos ?tab=audit vão pra lá
  if (activeTab === 'audit') return <Navigate to="/competitors/site-audit" replace />;

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('competitors.title', 'Analise de Concorrentes')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {t('competitors.subtitle', 'Monitore e analise seus concorrentes em tempo real')}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 min-w-0 shrink-0">
          <UsageBadge resource="competitors" />
          <Button
            variant="secondary"
            onClick={() => loadCompetitors()}
            leftIcon={<RefreshCw className="w-4 h-4" />}
            aria-label={t('common.refresh', 'Atualizar')}
            className="h-10 whitespace-nowrap flex-shrink-0 ml-auto sm:ml-0"
          >
            <span className="hidden sm:inline">{t('common.refresh', 'Atualizar')}</span>
          </Button>
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={handleOpenAddModal}
            className="h-10 whitespace-nowrap flex-shrink-0"
          >
            <span className="sm:hidden">{t('common.add', 'Adicionar')}</span>
            <span className="hidden sm:inline">{t('competitors.addCompetitor', 'Adicionar Concorrente')}</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card padding={false} className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-blue-600 dark:text-blue-400">
                  {t('competitors.stats.competitors', 'Concorrentes')}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.totalCompetitors}</p>
                <p className="hidden sm:block text-xs text-blue-600 dark:text-blue-400 mt-1">
                  {t('competitors.stats.activelyMonitored', 'monitorados ativamente')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 flex-shrink-0 bg-blue-100 dark:bg-blue-800 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card padding={false} className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-green-600 dark:text-green-400">
                  {t('competitors.stats.avgDA', 'DA Medio')}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-green-700 dark:text-green-300">{stats.avgDomainAuthority}</p>
                <p className="hidden sm:block text-xs text-green-600 dark:text-green-400 mt-1">
                  {t('competitors.stats.avgDAHint', 'autoridade média dos sites')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 flex-shrink-0 bg-green-100 dark:bg-green-800 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card padding={false} className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-purple-600 dark:text-purple-400">
                  {t('competitors.stats.totalTraffic', 'Trafego Total')}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-purple-700 dark:text-purple-300">{formatNumber(stats.totalOrganicTraffic)}</p>
                <p className="hidden sm:block text-xs text-purple-600 dark:text-purple-400 mt-1">
                  {t('competitors.stats.trafficHint', 'visitas estimadas por mês')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 flex-shrink-0 bg-purple-100 dark:bg-purple-800 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card padding={false} className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-800">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-orange-600 dark:text-orange-400">
                  {t('competitors.stats.backlinks', 'Backlinks')}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-orange-700 dark:text-orange-300">{formatNumber(stats.totalBacklinks)}</p>
                <p className="hidden sm:flex text-xs text-orange-600 dark:text-orange-400 items-center mt-1">
                  <Zap className="w-3 h-3 mr-1" />
                  {t('competitors.stats.sumAllCompetitors', 'soma de todos')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 flex-shrink-0 bg-orange-100 dark:bg-orange-800 rounded-xl flex items-center justify-center">
                <Link2 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ver a lista ou comparar lado a lado. As outras ferramentas (palavras-chave, posição
          no Google, auditoria...) são abas do topo da página (SectionTabs), não daqui. */}
      <div className="inline-flex w-full sm:w-auto rounded-xl bg-gray-100 dark:bg-gray-800 p-1" role="tablist">
        {tabs.filter((tab) => tab.id !== 'audit').map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Overview Tab - My Competitors */}
        {activeTab === 'overview' && (
          <div className="space-y-3 sm:space-y-6">
            {/* Search */}
            <Card>
              <CardContent className="p-3 sm:p-5">
                <form onSubmit={handleSearch} className="flex gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <Input
                      placeholder={t('competitors.searchPlaceholder', 'Buscar por nome ou dominio...')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      leftIcon={<Search className="w-5 h-5" />}
                    />
                  </div>
                  <Button type="submit" variant="secondary" className="whitespace-nowrap flex-shrink-0">
                    {t('common.search', 'Buscar')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Competitors Table */}
            {isLoading ? (
              <Card padding={false}>
                <div className="p-5 sm:p-8 space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                  ))}
                </div>
              </Card>
            ) : competitors.length === 0 ? (
              <div className="relative overflow-hidden rounded-2xl border border-primary-200/40 dark:border-primary-900/40 bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 dark:from-primary-950/30 dark:via-purple-950/20 dark:to-pink-950/20">
                {/* Decorative blurs */}
                <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 bg-primary-400/20 dark:bg-primary-500/10 rounded-full blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 -left-20 w-80 h-80 bg-pink-400/20 dark:bg-pink-500/10 rounded-full blur-3xl" />

                <div className="relative px-6 py-14 sm:px-10 sm:py-16 text-center max-w-3xl mx-auto">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-purple-600 shadow-xl shadow-primary-500/30 mb-6">
                    <Globe className="w-10 h-10 text-white" />
                  </div>

                  {/* Title + description */}
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                    {t('competitors.empty.title', 'Comece monitorando seu primeiro concorrente')}
                  </h2>
                  <p className="text-base text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto">
                    {t('competitors.empty.description', 'Acompanhe em tempo real: tráfego orgânico, autoridade de domínio, backlinks, palavras-chave e estratégia de conteúdo dos seus concorrentes diretos.')}
                  </p>

                  {/* Feature pills */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 max-w-2xl mx-auto">
                    {[
                      { icon: TrendingUp, key: 'traffic', fallback: 'Tráfego orgânico' },
                      { icon: BarChart3, key: 'keywords', fallback: 'Palavras-chave' },
                      { icon: Link2, key: 'backlinks', fallback: 'Backlinks' },
                      { icon: Activity, key: 'auth', fallback: 'Domain Authority' },
                    ].map(({ icon: Icon, key, fallback }) => (
                      <div
                        key={key}
                        className="flex flex-col items-center gap-1.5 p-3 bg-white/60 dark:bg-gray-900/40 backdrop-blur rounded-xl border border-white/50 dark:border-gray-700/50"
                      >
                        <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                          {t(`competitors.empty.feature.${key}`, fallback)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={handleOpenAddModal}
                    size="lg"
                    leftIcon={<Plus className="w-5 h-5" />}
                    className="shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-shadow"
                  >
                    {t('competitors.empty.cta', 'Adicionar primeiro concorrente')}
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                    {t('competitors.empty.hint', 'Só precisa do domínio — a análise é automática')}
                  </p>
                </div>
              </div>
            ) : (
              <Card padding={false}>
                <div className="overflow-x-auto scrollbar-hide">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          {t('competitors.table.competitor', 'Concorrente')}
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          DA
                        </th>
                        <th className="hidden sm:table-cell px-4 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          {t('competitors.table.organicTraffic', 'Trafego Organico')}
                        </th>
                        <th className="hidden sm:table-cell px-4 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          {t('competitors.table.backlinks', 'Backlinks')}
                        </th>
                        <th className="hidden sm:table-cell px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          {t('competitors.table.lastAnalysis', 'Ultima Analise')}
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-4"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {competitors.map((competitor) => (
                        <tr key={competitor.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="px-3 sm:px-6 py-2 sm:py-4">
                            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-lg flex items-center justify-center">
                                {competitor.logo_url ? (
                                  <img src={competitor.logo_url} alt={competitor.name} className="w-6 h-6 object-contain" />
                                ) : (
                                  <Globe className="w-5 h-5 text-gray-500" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <Link
                                  to={`/competitors/${competitor.id}`}
                                  className="block truncate max-w-[140px] sm:max-w-none font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                                >
                                  {competitor.name}
                                </Link>
                                <div className="flex items-center gap-2">
                                  <a
                                    href={`https://${competitor.domain}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 flex items-center gap-1 min-w-0"
                                  >
                                    <span className="truncate max-w-[120px] sm:max-w-none">{competitor.domain}</span>
                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                  </a>
                                  {competitor.industry && (
                                    <Badge variant="gray" className="hidden sm:inline-flex text-xs">{competitor.industry}</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                            <span className={`text-base sm:text-lg font-bold ${getDAColor(competitor.domain_authority || 0)}`}>
                              {competitor.domain_authority || '-'}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell px-4 py-4 text-right">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatNumber(competitor.organic_traffic || 0)}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell px-4 py-4 text-right">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatNumber(competitor.backlinks_count || 0)}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell px-4 py-4 text-center">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {competitor.last_analyzed_at
                                ? new Date(competitor.last_analyzed_at).toLocaleDateString()
                                : t('common.never', 'Nunca')}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Link to={`/competitors/${competitor.id}`} aria-label={t('common.moreDetails', 'Mais Detalhes')}>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="group whitespace-nowrap"
                                >
                                  <Eye className="w-4 h-4 sm:mr-1.5 group-hover:text-primary-600 transition-colors" />
                                  <span className="hidden sm:inline text-xs">{t('common.moreDetails', 'Mais Detalhes')}</span>
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEditModal(competitor)}
                                className="text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                                title={t('common.edit', 'Editar')}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(competitor.id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Benchmark Tab */}
        {activeTab === 'benchmark' && (
          <div className="space-y-3 sm:space-y-6">
            {(() => {
              // Filter only analyzed competitors
              const analyzedCompetitors = competitors.filter(c => c.last_analyzed_at && c.domain_authority);
              const referenceCompetitor = analyzedCompetitors.find(c => c.id === selectedForBenchmark[0]);
              const selectedComparisonIds = selectedForBenchmark.slice(1);
              const comparisonCompetitors = analyzedCompetitors.filter(c => selectedComparisonIds.includes(c.id));
              const availableForComparison = analyzedCompetitors.filter(c => c.id !== selectedForBenchmark[0]);

              // Calculate metrics for comparison
              const getMetricRank = (competitorId: string, metric: 'domain_authority' | 'organic_traffic' | 'backlinks_count') => {
                const sorted = [...analyzedCompetitors].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
                return sorted.findIndex(c => c.id === competitorId) + 1;
              };

              const getMetricMax = (metric: 'domain_authority' | 'organic_traffic' | 'backlinks_count') => {
                return Math.max(...analyzedCompetitors.map(c => c[metric] || 0));
              };

              const getCompetitiveScore = (competitor: Competitor) => {
                const daScore = ((competitor.domain_authority || 0) / Math.max(getMetricMax('domain_authority'), 1)) * 35;
                const trafficScore = ((competitor.organic_traffic || 0) / Math.max(getMetricMax('organic_traffic'), 1)) * 40;
                const backlinksScore = ((competitor.backlinks_count || 0) / Math.max(getMetricMax('backlinks_count'), 1)) * 25;
                return Math.round(daScore + trafficScore + backlinksScore);
              };

              if (analyzedCompetitors.length === 0) {
                return (
                  <Card>
                    <CardContent className="text-center py-16">
                      <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <GitCompare className="w-10 h-10 text-indigo-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {t('competitors.noAnalyzedCompetitors', 'Nenhum concorrente analisado')}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                        {t('competitors.analyzeFirstForBenchmark', 'Para comparar concorrentes, primeiro você precisa analisar pelo menos 2 concorrentes. Vá para a aba "Análise Completa" e clique em "Analisar" em cada concorrente.')}
                      </p>
                      <Button onClick={() => setActiveTab('overview')}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        {t('competitors.goToAnalysis', 'Ir para Análise')}
                      </Button>
                    </CardContent>
                  </Card>
                );
              }

              if (analyzedCompetitors.length === 1) {
                return (
                  <Card>
                    <CardContent className="text-center py-16">
                      <div className="w-20 h-20 bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertTriangle className="w-10 h-10 text-yellow-500" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {t('competitors.needMoreAnalyzed', 'Precisa de mais concorrentes')}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                        {t('competitors.analyzeMoreForBenchmark', 'Você tem apenas 1 concorrente analisado. Analise pelo menos mais 1 para fazer comparações.')}
                      </p>
                      <Button onClick={() => setActiveTab('overview')}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        {t('competitors.goToAnalysis', 'Ir para Análise')}
                      </Button>
                    </CardContent>
                  </Card>
                );
              }

              return (
                <>
                  {/* Reference Site Selector */}
                  <Card className="overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-4 sm:p-6">
                      <h2 className="text-lg sm:text-xl font-bold text-white mb-2 flex items-center gap-3">
                        <Target className="w-6 h-6" />
                        {t('competitors.selectReference', 'Selecione o Site de Referência')}
                      </h2>
                      <p className="text-white/80 text-sm">
                        {t('competitors.referenceDescription', 'Escolha qual concorrente será usado como base para comparação. Todos os outros serão comparados a ele.')}
                      </p>
                    </div>
                    <CardContent className="p-3 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {analyzedCompetitors.map((competitor, idx) => {
                          const isSelected = selectedForBenchmark[0] === competitor.id;
                          const score = getCompetitiveScore(competitor);
                          return (
                            <button
                              key={competitor.id}
                              onClick={() => setSelectedForBenchmark([competitor.id])}
                              className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all text-left group min-w-0 ${
                                isSelected
                                  ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 shadow-lg shadow-indigo-500/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md'
                              }`}
                            >
                              {isSelected && (
                                <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                                  <CheckCircle className="w-5 h-5 text-white" />
                                </div>
                              )}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-xl flex items-center justify-center ${
                                    isSelected
                                      ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                                      : 'bg-gray-100 dark:bg-gray-800 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/30'
                                  }`}>
                                    <Globe className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-500 group-hover:text-indigo-600'}`} />
                                  </div>
                                  <div className="min-w-0">
                                    <h3 className="font-bold text-gray-900 dark:text-white truncate">{competitor.name}</h3>
                                    <p className="text-xs text-gray-500 truncate">{competitor.domain}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">Score Competitivo</span>
                                  <span className={`font-bold ${score >= 70 ? 'text-green-600' : score >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                                    {score}/100
                                  </span>
                                </div>
                                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${
                                      score >= 70 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                                      score >= 40 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                                      'bg-gradient-to-r from-red-400 to-rose-500'
                                    }`}
                                    style={{ width: `${score}%` }}
                                  />
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-2">
                                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <p className="text-xs text-gray-500">DA</p>
                                    <p className="font-bold text-gray-900 dark:text-white">{competitor.domain_authority || 0}</p>
                                  </div>
                                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <p className="text-xs text-gray-500">Tráfego</p>
                                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(competitor.organic_traffic || 0)}</p>
                                  </div>
                                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <p className="text-xs text-gray-500">Links</p>
                                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(competitor.backlinks_count || 0)}</p>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Competitor Selection */}
                  {referenceCompetitor && availableForComparison.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          {t('competitors.selectToCompare', 'Selecione os Concorrentes para Comparar')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm">
                          {t('competitors.selectToCompareDesc', 'Escolha quais concorrentes você quer comparar com')} <span className="font-bold text-indigo-600">{referenceCompetitor.name}</span>
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                          {availableForComparison.map((competitor) => {
                            const isSelected = selectedComparisonIds.includes(competitor.id);
                            return (
                              <button
                                key={competitor.id}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedForBenchmark(selectedForBenchmark.filter(id => id !== competitor.id));
                                  } else {
                                    setSelectedForBenchmark([...selectedForBenchmark, competitor.id]);
                                  }
                                }}
                                className={`p-2.5 sm:p-4 min-w-0 rounded-xl border-2 transition-all text-left ${
                                  isSelected
                                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-700'
                                }`}
                              >
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <div className={`w-6 h-6 flex-shrink-0 rounded-lg border-2 flex items-center justify-center transition-all ${
                                    isSelected
                                      ? 'bg-green-500 border-green-500'
                                      : 'border-gray-300 dark:border-gray-600'
                                  }`}>
                                    {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{competitor.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{competitor.domain}</p>
                                  </div>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-1 text-center text-[11px] sm:text-xs">
                                  <div className="bg-gray-100 dark:bg-gray-800 rounded p-1">
                                    <p className="text-gray-500">DA</p>
                                    <p className="font-bold text-gray-900 dark:text-white">{competitor.domain_authority || 0}</p>
                                  </div>
                                  <div className="bg-gray-100 dark:bg-gray-800 rounded p-1">
                                    <p className="text-gray-500">Tráf.</p>
                                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(competitor.organic_traffic || 0)}</p>
                                  </div>
                                  <div className="bg-gray-100 dark:bg-gray-800 rounded p-1">
                                    <p className="text-gray-500">Links</p>
                                    <p className="font-bold text-gray-900 dark:text-white">{formatNumber(competitor.backlinks_count || 0)}</p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedComparisonIds.length > 0 && (
                          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                            <p className="text-sm text-green-700 dark:text-green-400">
                              <CheckCircle className="w-4 h-4 inline mr-2" />
                              {selectedComparisonIds.length} {selectedComparisonIds.length === 1 ? 'concorrente selecionado' : 'concorrentes selecionados'} para comparação
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {referenceCompetitor && comparisonCompetitors.length > 0 && (
                    <>
                      {(() => {
                        // Combined set for comparison calculations
                        const comparisonSet = [referenceCompetitor, ...comparisonCompetitors];

                        // Helper to get latest analysis data
                        const getAnalysisData = (competitor: Competitor) => {
                          const latestAnalysis = competitor.analyses?.sort((a, b) =>
                            new Date(b.date).getTime() - new Date(a.date).getTime()
                          )[0];
                          return {
                            referring_domains: latestAnalysis?.referring_domains || 0,
                            total_keywords: latestAnalysis?.total_keywords || 0,
                            traffic_value: latestAnalysis?.traffic_value || 0,
                            top_keywords: latestAnalysis?.top_keywords || [],
                            top_pages: latestAnalysis?.top_pages || [],
                          };
                        };

                        // Extended metrics including analysis data
                        type MetricKey = 'domain_authority' | 'organic_traffic' | 'paid_traffic' | 'backlinks_count' | 'referring_domains' | 'total_keywords' | 'traffic_value';

                        const getMetricValue = (competitor: Competitor, metric: MetricKey): number => {
                          if (['referring_domains', 'total_keywords', 'traffic_value'].includes(metric)) {
                            const analysis = getAnalysisData(competitor);
                            return analysis[metric as keyof typeof analysis] as number || 0;
                          }
                          return (competitor[metric as keyof Competitor] as number) || 0;
                        };

                        const getComparisonRank = (competitorId: string, metric: MetricKey) => {
                          const sorted = [...comparisonSet].sort((a, b) => getMetricValue(b, metric) - getMetricValue(a, metric));
                          return sorted.findIndex(c => c.id === competitorId) + 1;
                        };

                        const getComparisonMax = (metric: MetricKey) => {
                          const values = comparisonSet.map(c => getMetricValue(c, metric));
                          return Math.max(...values, 1);
                        };

                        const getComparisonScore = (competitor: Competitor) => {
                          const daMax = getComparisonMax('domain_authority');
                          const trafficMax = getComparisonMax('organic_traffic');
                          const backlinksMax = getComparisonMax('backlinks_count');
                          const keywordsMax = getComparisonMax('total_keywords');

                          const daScore = (getMetricValue(competitor, 'domain_authority') / daMax) * 25;
                          const trafficScore = (getMetricValue(competitor, 'organic_traffic') / trafficMax) * 30;
                          const backlinksScore = (getMetricValue(competitor, 'backlinks_count') / backlinksMax) * 25;
                          const keywordsScore = (getMetricValue(competitor, 'total_keywords') / keywordsMax) * 20;

                          return Math.round(daScore + trafficScore + backlinksScore + keywordsScore);
                        };

                        // All metrics for display
                        const metrics: { key: MetricKey; label: string; icon: any; color: string; format: (v: number) => string }[] = [
                          { key: 'domain_authority', label: t('competitors.domainAuthority', 'Autoridade'), icon: Shield, color: 'indigo', format: (v) => String(v) },
                          { key: 'organic_traffic', label: t('competitors.organicTraffic', 'Tráfego Orgânico'), icon: Users, color: 'green', format: formatNumber },
                          { key: 'paid_traffic', label: t('competitors.paidTraffic', 'Tráfego Pago'), icon: Megaphone, color: 'orange', format: formatNumber },
                          { key: 'backlinks_count', label: 'Backlinks', icon: Link2, color: 'purple', format: formatNumber },
                          { key: 'referring_domains', label: t('competitors.referringDomains', 'Domínios Referentes'), icon: Globe, color: 'blue', format: formatNumber },
                          { key: 'total_keywords', label: t('competitors.totalKeywords', 'Keywords Totais'), icon: Target, color: 'pink', format: formatNumber },
                          { key: 'traffic_value', label: t('competitors.trafficValue', 'Valor do Tráfego'), icon: Zap, color: 'yellow', format: (v) => `$${formatNumber(v)}` },
                        ];

                        return (
                          <>
                      {/* Competitive Score Overview */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card className="md:col-span-1 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white border-0">
                          <CardContent className="pt-6 pb-6 text-center">
                            <div className="w-20 h-20 mx-auto mb-4 relative">
                              <svg className="w-20 h-20 transform -rotate-90">
                                <circle cx="40" cy="40" r="35" stroke="rgba(255,255,255,0.2)" strokeWidth="6" fill="none" />
                                <circle
                                  cx="40" cy="40" r="35"
                                  stroke="white"
                                  strokeWidth="6"
                                  fill="none"
                                  strokeDasharray={`${getComparisonScore(referenceCompetitor) * 2.2} 220`}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-2xl font-bold">{getComparisonScore(referenceCompetitor)}</span>
                              </div>
                            </div>
                            <h3 className="font-bold text-lg mb-1">{referenceCompetitor.name}</h3>
                            <p className="text-white/70 text-sm">{t('competitors.competitiveScore', 'Score Competitivo')}</p>
                          </CardContent>
                        </Card>

                        <Card className="md:col-span-3">
                          <CardContent className="pt-6">
                            <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-indigo-500" />
                              {t('competitors.rankingPosition', 'Posição no Ranking')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {metrics.map((metric) => {
                                const rank = getComparisonRank(referenceCompetitor.id, metric.key);
                                const total = comparisonSet.length;
                                const value = getMetricValue(referenceCompetitor, metric.key);
                                const maxValue = getComparisonMax(metric.key);
                                const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
                                const Icon = metric.icon;
                                return (
                                  <div key={metric.key} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <div className={`w-9 h-9 rounded-lg bg-${metric.color}-100 dark:bg-${metric.color}-900/30 flex items-center justify-center flex-shrink-0`}>
                                      <Icon className={`w-4 h-4 text-${metric.color}-600`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{metric.label}</span>
                                        <Badge variant={rank === 1 ? 'success' : rank <= 2 ? 'warning' : 'gray'} className="text-xs">
                                          #{rank}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full bg-gradient-to-r ${
                                              rank === 1 ? 'from-green-400 to-emerald-500' :
                                              rank <= 2 ? 'from-yellow-400 to-orange-500' :
                                              'from-gray-400 to-gray-500'
                                            }`}
                                            style={{ width: `${percentage}%` }}
                                          />
                                        </div>
                                        <span className="text-xs font-bold text-gray-900 dark:text-white whitespace-nowrap">
                                          {metric.format(value)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Comparison vs Others */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <GitCompare className="w-5 h-5" />
                            {t('competitors.comparisonVsOthers', 'Comparativo com Concorrentes')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-900">
                                    {t('competitors.competitor', 'Concorrente')}
                                  </th>
                                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-900 dark:text-white">
                                    {t('competitors.score', 'Score')}
                                  </th>
                                  {metrics.map(m => (
                                    <th key={m.key} className="px-3 py-3 text-center text-xs font-bold text-gray-900 dark:text-white whitespace-nowrap">
                                      {m.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {/* Reference Row */}
                                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
                                  <td className="px-3 py-3 sticky left-0 bg-indigo-50 dark:bg-indigo-900/20">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                                        <Target className="w-4 h-4 text-white" />
                                      </div>
                                      <div>
                                        <p className="font-bold text-gray-900 dark:text-white text-sm">{referenceCompetitor.name}</p>
                                        <p className="text-xs text-indigo-600 dark:text-indigo-400">★ {t('competitors.reference', 'Referência')}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold text-sm">
                                      {getComparisonScore(referenceCompetitor)}
                                    </span>
                                  </td>
                                  {metrics.map(m => (
                                    <td key={m.key} className="px-3 py-3 text-center">
                                      <span className="font-bold text-gray-900 dark:text-white text-sm">
                                        {m.format(getMetricValue(referenceCompetitor, m.key))}
                                      </span>
                                    </td>
                                  ))}
                                </tr>
                                {/* Other Competitors */}
                                {comparisonCompetitors.map((competitor) => {
                                  const refScore = getComparisonScore(referenceCompetitor);
                                  const compScore = getComparisonScore(competitor);
                                  const scoreDiff = compScore - refScore;

                                  return (
                                    <tr key={competitor.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                      <td className="px-3 py-3 sticky left-0 bg-white dark:bg-gray-900">
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                                            <Globe className="w-4 h-4 text-gray-500" />
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-900 dark:text-white text-sm">{competitor.name}</p>
                                            <p className="text-xs text-gray-500">{competitor.domain}</p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-3 py-3 text-center">
                                        <div className="flex flex-col items-center">
                                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                                            compScore >= 70 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                            compScore >= 40 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                          }`}>
                                            {compScore}
                                          </span>
                                          <span className={`text-xs mt-1 ${scoreDiff > 0 ? 'text-red-500' : scoreDiff < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                                            {scoreDiff > 0 ? `+${scoreDiff}` : scoreDiff < 0 ? scoreDiff : '='}
                                          </span>
                                        </div>
                                      </td>
                                      {metrics.map(m => {
                                        const refValue = getMetricValue(referenceCompetitor, m.key);
                                        const compValue = getMetricValue(competitor, m.key);
                                        const diff = compValue - refValue;
                                        const pctDiff = refValue > 0 ? Math.round((diff / refValue) * 100) : 0;

                                        return (
                                          <td key={m.key} className="px-3 py-3 text-center">
                                            <div className="flex flex-col items-center">
                                              <span className="font-medium text-gray-900 dark:text-white text-sm">
                                                {m.format(compValue)}
                                              </span>
                                              <span className={`text-xs ${diff > 0 ? 'text-red-500' : diff < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                                                {pctDiff > 0 ? `+${pctDiff}%` : pctDiff < 0 ? `${pctDiff}%` : '='}
                                              </span>
                                            </div>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Metrics Comparison Bars */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {metrics.slice(0, 4).map((metric) => {
                          const maxValue = getComparisonMax(metric.key);
                          const Icon = metric.icon;
                          const sortedByMetric = [...comparisonSet].sort((a, b) =>
                            getMetricValue(b, metric.key) - getMetricValue(a, metric.key)
                          );

                          return (
                            <Card key={metric.key}>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  {metric.label}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {sortedByMetric.map((competitor, idx) => {
                                    const value = getMetricValue(competitor, metric.key);
                                    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
                                    const isReference = competitor.id === referenceCompetitor.id;

                                    return (
                                      <div key={competitor.id}>
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-1">
                                            {idx === 0 && <span className="text-xs">👑</span>}
                                            <span className={`text-xs truncate max-w-[80px] ${isReference ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                              {competitor.name}
                                            </span>
                                          </div>
                                          <span className="text-xs font-bold text-gray-900 dark:text-white">
                                            {metric.format(value)}
                                          </span>
                                        </div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full rounded-full transition-all duration-500 ${
                                              isReference ? 'bg-gradient-to-r from-indigo-500 to-purple-600' : 'bg-gray-400'
                                            }`}
                                            style={{ width: `${percentage}%` }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Radar Chart */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            {t('competitors.competitiveRadar', 'Radar Competitivo')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {(() => {
                            const radarData = [
                              {
                                metric: t('competitors.domainAuthority', 'Domain Authority'),
                                ...Object.fromEntries(comparisonSet.map(c => [
                                  c.name,
                                  Math.round(((c.domain_authority || 0) / Math.max(getComparisonMax('domain_authority'), 1)) * 100)
                                ]))
                              },
                              {
                                metric: t('competitors.traffic', 'Tráfego'),
                                ...Object.fromEntries(comparisonSet.map(c => [
                                  c.name,
                                  Math.round(((c.organic_traffic || 0) / Math.max(getComparisonMax('organic_traffic'), 1)) * 100)
                                ]))
                              },
                              {
                                metric: 'Backlinks',
                                ...Object.fromEntries(comparisonSet.map(c => [
                                  c.name,
                                  Math.round(((c.backlinks_count || 0) / Math.max(getComparisonMax('backlinks_count'), 1)) * 100)
                                ]))
                              },
                              {
                                metric: t('competitors.totalKeywords', 'Keywords'),
                                ...Object.fromEntries(comparisonSet.map(c => [
                                  c.name,
                                  Math.round((getMetricValue(c, 'total_keywords') / Math.max(getComparisonMax('total_keywords'), 1)) * 100)
                                ]))
                              },
                            ];

                            return (
                              <ResponsiveContainer width="100%" height={400}>
                                <RadarChart data={radarData}>
                                  <PolarGrid strokeDasharray="3 3" />
                                  <PolarAngleAxis dataKey="metric" tick={{ fill: 'currentColor', fontSize: 12 }} />
                                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                                  {comparisonSet.map((c, idx) => (
                                    <Radar
                                      key={c.id}
                                      name={c.name}
                                      dataKey={c.name}
                                      stroke={c.id === referenceCompetitor.id ? '#6366F1' : COLORS[idx % COLORS.length]}
                                      fill={c.id === referenceCompetitor.id ? '#6366F1' : COLORS[idx % COLORS.length]}
                                      fillOpacity={c.id === referenceCompetitor.id ? 0.4 : 0.2}
                                      strokeWidth={c.id === referenceCompetitor.id ? 3 : 1}
                                    />
                                  ))}
                                  <Legend />
                                  <Tooltip />
                                </RadarChart>
                              </ResponsiveContainer>
                            );
                          })()}
                        </CardContent>
                      </Card>

                      {/* Insights */}
                      <Card className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white border-0 overflow-hidden">
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex items-start gap-5">
                            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-7 h-7" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold mb-4">{t('competitors.competitiveInsights', 'Insights Competitivos')}</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(() => {
                                  const insights = [];
                                  const refScore = getComparisonScore(referenceCompetitor);
                                  const avgScore = comparisonCompetitors.length > 0
                                    ? comparisonCompetitors.reduce((sum, c) => sum + getComparisonScore(c), 0) / comparisonCompetitors.length
                                    : refScore;
                                  const daRank = getComparisonRank(referenceCompetitor.id, 'domain_authority');
                                  const trafficRank = getComparisonRank(referenceCompetitor.id, 'organic_traffic');
                                  const backlinksRank = getComparisonRank(referenceCompetitor.id, 'backlinks_count');

                                  if (refScore > avgScore) {
                                    insights.push({
                                      icon: '🏆',
                                      title: t('competitors.insights.aboveAverage', 'Acima da média'),
                                      desc: t('competitors.insights.aboveAverageDesc', 'Score {{points}} pontos acima da média dos concorrentes', { points: Math.round(refScore - avgScore) })
                                    });
                                  } else if (refScore < avgScore) {
                                    insights.push({
                                      icon: '📈',
                                      title: t('competitors.insights.growthOpportunity', 'Oportunidade de crescimento'),
                                      desc: t('competitors.insights.growthOpportunityDesc', 'Potencial de {{points}} pontos para alcançar a média', { points: Math.round(avgScore - refScore) })
                                    });
                                  } else {
                                    insights.push({
                                      icon: '⚖️',
                                      title: t('competitors.insights.atAverage', 'Na média'),
                                      desc: t('competitors.insights.atAverageDesc', 'Score alinhado com a média dos concorrentes')
                                    });
                                  }

                                  if (daRank === 1) {
                                    insights.push({
                                      icon: '👑',
                                      title: t('competitors.insights.authorityLeader', 'Líder em Autoridade'),
                                      desc: t('competitors.insights.authorityLeaderDesc', 'Maior Domain Authority entre os concorrentes')
                                    });
                                  } else {
                                    const leader = [...comparisonSet].sort((a, b) => (b.domain_authority || 0) - (a.domain_authority || 0))[0];
                                    insights.push({
                                      icon: '🔗',
                                      title: t('competitors.insights.backlinkFocus', 'Foco em backlinks'),
                                      desc: t('competitors.insights.backlinkFocusDesc', '{{points}} pontos de DA atrás do líder ({{leader}})', {
                                        points: (leader.domain_authority || 0) - (referenceCompetitor.domain_authority || 0),
                                        leader: leader.name
                                      })
                                    });
                                  }

                                  if (trafficRank === 1) {
                                    insights.push({
                                      icon: '🚀',
                                      title: t('competitors.insights.trafficLeader', 'Líder em Tráfego'),
                                      desc: t('competitors.insights.trafficLeaderDesc', 'Maior tráfego orgânico do grupo')
                                    });
                                  } else {
                                    const leader = [...comparisonSet].sort((a, b) => (b.organic_traffic || 0) - (a.organic_traffic || 0))[0];
                                    const gap = (leader.organic_traffic || 0) - (referenceCompetitor.organic_traffic || 0);
                                    insights.push({
                                      icon: '🎯',
                                      title: t('competitors.insights.trafficGap', 'Gap de tráfego'),
                                      desc: t('competitors.insights.trafficGapDesc', '{{visits}} visitas atrás do líder ({{leader}})', {
                                        visits: formatNumber(gap),
                                        leader: leader.name
                                      })
                                    });
                                  }

                                  if (backlinksRank === 1) {
                                    insights.push({
                                      icon: '🔥',
                                      title: t('competitors.insights.linksLeader', 'Líder em Links'),
                                      desc: t('competitors.insights.linksLeaderDesc', 'Maior quantidade de backlinks')
                                    });
                                  } else {
                                    insights.push({
                                      icon: '💡',
                                      title: t('competitors.insights.linkBuildingStrategy', 'Estratégia de link building'),
                                      desc: t('competitors.insights.linkBuildingStrategyDesc', 'Invista em parcerias e guest posts para aumentar backlinks')
                                    });
                                  }

                                  return insights.map((insight, i) => (
                                    <div key={i} className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
                                      <span className="text-2xl">{insight.icon}</span>
                                      <div>
                                        <h4 className="font-semibold">{insight.title}</h4>
                                        <p className="text-sm text-white/80">{insight.desc}</p>
                                      </div>
                                    </div>
                                  ));
                                })()}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                          </>
                        );
                      })()}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Audit Tab */}
        {(activeTab as TabType) === 'audit' && (
          <div className="space-y-3 sm:space-y-6">
            {/* Audit Search */}
            <Card>
              <CardContent className="p-3 sm:p-5">
                <form onSubmit={handleRunAudit} className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <Input
                      placeholder={t('competitors.enterUrlForAudit', 'Digite uma URL para auditar (ex: https://exemplo.com.br)')}
                      value={auditUrl}
                      onChange={(e) => setAuditUrl(e.target.value)}
                      leftIcon={<FileSearch className="w-5 h-5" />}
                    />
                  </div>
                  <div className="flex gap-2 sm:gap-4">
                  <Button type="submit" disabled={auditLoading || !auditUrl.trim()} className="flex-1 sm:flex-none h-10 whitespace-nowrap">
                    {auditLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Activity className="w-4 h-4 mr-2" />
                    )}
                    <span className="sm:hidden">{t('competitors.startAuditShort', 'Auditar')}</span>
                    <span className="hidden sm:inline">{t('competitors.startAudit', 'Iniciar Auditoria')}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 whitespace-nowrap flex-shrink-0"
                    aria-label={t('competitors.history', 'Historico')}
                    onClick={() => {
                      setShowAuditHistory(!showAuditHistory);
                      if (!showAuditHistory && auditHistory.length === 0) {
                        loadAuditHistory(1);
                      }
                    }}
                  >
                    <History className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('competitors.history', 'Historico')}</span>
                  </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* History Section */}
            {showAuditHistory && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    {t('competitors.previousAudits', 'Auditorias Anteriores')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                    </div>
                  ) : auditHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {t('competitors.noHistory', 'Nenhuma auditoria encontrada')}
                    </div>
                  ) : (
                    <div className="overflow-x-auto scrollbar-hide">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">URL</th>
                            <th className="text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">Score</th>
                            <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">Mobile</th>
                            <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">Desktop</th>
                            <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('competitors.issues', 'Problemas')}</th>
                            <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.date', 'Data')}</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.actions', 'Acoes')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditHistory.map((item) => (
                            <tr key={item.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-3 px-2">
                                <span className="text-sm text-gray-900 dark:text-white truncate max-w-[160px] sm:max-w-xs block">
                                  {item.url}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-center">
                                <span className={`font-bold ${getScoreColor(item.scores?.overall || 0)}`}>
                                  {item.scores?.overall || 0}
                                </span>
                              </td>
                              <td className="hidden sm:table-cell py-3 px-2 text-center">
                                <span className={`text-sm ${getScoreColor(item.page_speed_mobile || 0)}`}>
                                  {item.page_speed_mobile || 0}
                                </span>
                              </td>
                              <td className="hidden sm:table-cell py-3 px-2 text-center">
                                <span className={`text-sm ${getScoreColor(item.page_speed_desktop || 0)}`}>
                                  {item.page_speed_desktop || 0}
                                </span>
                              </td>
                              <td className="hidden sm:table-cell py-3 px-2 text-center">
                                <div className="flex items-center justify-center gap-2 text-xs">
                                  {item.critical_issues > 0 && (
                                    <span className="text-red-500">{item.critical_issues} criticos</span>
                                  )}
                                  {item.warnings > 0 && (
                                    <span className="text-yellow-500">{item.warnings} avisos</span>
                                  )}
                                </div>
                              </td>
                              <td className="hidden sm:table-cell py-3 px-2 text-center text-sm text-gray-500 dark:text-gray-400">
                                {new Date(item.created_at).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => loadAuditFromHistory(item.id)}
                                    disabled={loadingAuditId === item.id}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                    title={t('common.view', 'Ver')}
                                  >
                                    {loadingAuditId === item.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => deleteAuditFromHistory(item.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                    title={t('common.delete', 'Excluir')}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {auditLoading && (
              <Card>
                <CardContent className="py-16">
                  <div className="text-center">
                    <Loader2 className="w-16 h-16 animate-spin text-primary-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('competitors.analyzing', 'Analisando site...')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('competitors.analyzingDesc', 'Isso pode levar alguns segundos')}
                    </p>
                    <div className="mt-6 max-w-md mx-auto">
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>{t('competitors.checkingSEO', 'Verificando SEO On-Page')}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                        <span>{t('competitors.checkingPerformance', 'Analisando performance')}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-400 dark:text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>{t('competitors.checkingLinks', 'Verificando links')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Full Audit Results */}
            {fullAuditResult && !auditLoading && (
              <>
                {/* Device Toggle */}
                <Card>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-center gap-2">
                      <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400 mr-2">
                        {t('competitors.viewAs', 'Visualizar como')}:
                      </span>
                      <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                        <button
                          onClick={() => setSelectedDevice('mobile')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            selectedDevice === 'mobile'
                              ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Smartphone className="w-4 h-4" />
                          Mobile
                        </button>
                        <button
                          onClick={() => setSelectedDevice('desktop')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            selectedDevice === 'desktop'
                              ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Globe className="w-4 h-4" />
                          Desktop
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Score Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Overall Score */}
                  <Card className="md:col-span-1">
                    <CardContent className="pt-6 text-center">
                      <div className="relative inline-flex items-center justify-center">
                        <svg className="w-32 h-32">
                          <circle
                            className="text-gray-200 dark:text-gray-700"
                            strokeWidth="10"
                            stroke="currentColor"
                            fill="transparent"
                            r="52"
                            cx="64"
                            cy="64"
                          />
                          <circle
                            className={getScoreColor(currentAuditData?.overallScore || 0)}
                            strokeWidth="10"
                            strokeDasharray={`${(currentAuditData?.overallScore || 0) * 3.27} 327`}
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="transparent"
                            r="52"
                            cx="64"
                            cy="64"
                            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                          />
                        </svg>
                        <span className={`absolute text-3xl font-bold ${getScoreColor(currentAuditData?.overallScore || 0)}`}>
                          {currentAuditData?.overallScore || 0}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {t('competitors.overallScore', 'Score Geral')}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Issues Summary */}
                  <Card className="md:col-span-2">
                    <CardContent className="pt-6">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                        {t('competitors.issuesSummary', 'Resumo de Problemas')}
                      </h3>
                      <div className="grid grid-cols-4 gap-2 sm:gap-4">
                        <div className="text-center">
                          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                            <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-500" />
                          </div>
                          <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">
                            {currentAuditData?.totalIssues?.critical || 0}
                          </p>
                          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                            {t('competitors.critical', 'Criticos')}
                          </p>
                        </div>
                        <div className="text-center">
                          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                            <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-500" />
                          </div>
                          <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                            {currentAuditData?.totalIssues?.warning || 0}
                          </p>
                          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                            {t('competitors.warnings', 'Avisos')}
                          </p>
                        </div>
                        <div className="text-center">
                          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                            <Info className="w-4 h-4 sm:w-6 sm:h-6 text-blue-500" />
                          </div>
                          <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {currentAuditData?.totalIssues?.info || 0}
                          </p>
                          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                            {t('competitors.notices', 'Observacoes')}
                          </p>
                        </div>
                        <div className="text-center">
                          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                            <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-500" />
                          </div>
                          <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                            {currentAuditData?.totalIssues?.passed || 0}
                          </p>
                          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                            {t('competitors.passed', 'Passou')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Page Speed */}
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                        {t('competitors.pageSpeed', 'PageSpeed')}
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                              <Smartphone className="w-4 h-4" />
                              Mobile
                            </span>
                            <span className={`font-bold ${getScoreColor(fullAuditResult.pageSpeed?.mobile || 0)}`}>
                              {fullAuditResult.pageSpeed?.mobile || 0}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getScoreBg(fullAuditResult.pageSpeed?.mobile || 0)}`}
                              style={{ width: `${fullAuditResult.pageSpeed?.mobile || 0}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                              <Globe className="w-4 h-4" />
                              Desktop
                            </span>
                            <span className={`font-bold ${getScoreColor(fullAuditResult.pageSpeed?.desktop || 0)}`}>
                              {fullAuditResult.pageSpeed?.desktop || 0}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getScoreBg(fullAuditResult.pageSpeed?.desktop || 0)}`}
                              style={{ width: `${fullAuditResult.pageSpeed?.desktop || 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Core Web Vitals */}
                {currentAuditData?.coreWebVitals && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Core Web Vitals
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
                        {currentAuditData.coreWebVitals.lcp && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">LCP</p>
                            <p className={`text-xl font-bold ${getScoreColor(currentAuditData.coreWebVitals.lcp.score)}`}>
                              {currentAuditData.coreWebVitals.lcp.displayValue}
                            </p>
                            <p className="text-xs text-gray-500">Largest Contentful Paint</p>
                          </div>
                        )}
                        {currentAuditData.coreWebVitals.cls && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">CLS</p>
                            <p className={`text-xl font-bold ${getScoreColor(currentAuditData.coreWebVitals.cls.score)}`}>
                              {currentAuditData.coreWebVitals.cls.displayValue}
                            </p>
                            <p className="text-xs text-gray-500">Cumulative Layout Shift</p>
                          </div>
                        )}
                        {currentAuditData.coreWebVitals.fid && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">FID</p>
                            <p className={`text-xl font-bold ${getScoreColor(currentAuditData.coreWebVitals.fid.score)}`}>
                              {currentAuditData.coreWebVitals.fid.displayValue}
                            </p>
                            <p className="text-xs text-gray-500">First Input Delay</p>
                          </div>
                        )}
                        {currentAuditData.coreWebVitals.ttfb && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">TTFB</p>
                            <p className={`text-xl font-bold ${getScoreColor(currentAuditData.coreWebVitals.ttfb.score)}`}>
                              {currentAuditData.coreWebVitals.ttfb.displayValue}
                            </p>
                            <p className="text-xs text-gray-500">Time to First Byte</p>
                          </div>
                        )}
                        {currentAuditData.coreWebVitals.speedIndex && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">Speed Index</p>
                            <p className={`text-xl font-bold ${getScoreColor(currentAuditData.coreWebVitals.speedIndex.score)}`}>
                              {currentAuditData.coreWebVitals.speedIndex.displayValue}
                            </p>
                            <p className="text-xs text-gray-500">Visual Speed</p>
                          </div>
                        )}
                        {currentAuditData.coreWebVitals.tbt && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">TBT</p>
                            <p className={`text-xl font-bold ${getScoreColor(currentAuditData.coreWebVitals.tbt.score)}`}>
                              {currentAuditData.coreWebVitals.tbt.displayValue}
                            </p>
                            <p className="text-xs text-gray-500">Total Blocking Time</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Screenshot */}
                {currentAuditData?.screenshot?.final && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Image className="w-5 h-5" />
                        {t('competitors.screenshot', 'Screenshot')} ({selectedDevice === 'mobile' ? 'Mobile' : 'Desktop'})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden max-w-md mx-auto">
                        <img
                          src={currentAuditData.screenshot.final}
                          alt={`Site Screenshot - ${selectedDevice}`}
                          className="w-full h-auto rounded-lg shadow-inner"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* SEO Analysis */}
                {fullAuditResult.seoAnalysis && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        {t('competitors.seoAnalysis', 'Análise SEO')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                        {/* Meta Tags */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Code className="w-4 h-4" />
                            Meta Tags
                          </h4>
                          <div className="space-y-3">
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Title</span>
                                <Badge variant={fullAuditResult.seoAnalysis.meta.titleLength >= 30 && fullAuditResult.seoAnalysis.meta.titleLength <= 60 ? 'success' : 'warning'}>
                                  {fullAuditResult.seoAnalysis.meta.titleLength} chars
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                                {fullAuditResult.seoAnalysis.meta.title || <span className="text-red-500">Faltando</span>}
                              </p>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Meta Description</span>
                                <Badge variant={fullAuditResult.seoAnalysis.meta.descriptionLength >= 120 && fullAuditResult.seoAnalysis.meta.descriptionLength <= 160 ? 'success' : 'warning'}>
                                  {fullAuditResult.seoAnalysis.meta.descriptionLength} chars
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                                {fullAuditResult.seoAnalysis.meta.description || <span className="text-red-500">Faltando</span>}
                              </p>
                            </div>
                            {fullAuditResult.seoAnalysis.meta.canonical && (
                              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Canonical</span>
                                <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
                                  {fullAuditResult.seoAnalysis.meta.canonical}
                                </p>
                              </div>
                            )}
                            {(fullAuditResult.seoAnalysis.meta.ogTitle || fullAuditResult.seoAnalysis.meta.ogDescription) && (
                              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <span className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1">
                                  Open Graph
                                  <CheckCircle className="w-3 h-3" />
                                </span>
                                {fullAuditResult.seoAnalysis.meta.ogTitle && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    <strong>og:title:</strong> {fullAuditResult.seoAnalysis.meta.ogTitle}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Headings Structure */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Estrutura de Headings
                          </h4>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <span className="text-sm font-mono">H1</span>
                              <Badge variant={fullAuditResult.seoAnalysis.headings.h1Count === 1 ? 'success' : fullAuditResult.seoAnalysis.headings.h1Count === 0 ? 'danger' : 'warning'}>
                                {fullAuditResult.seoAnalysis.headings.h1Count}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <span className="text-sm font-mono">H2</span>
                              <Badge variant="primary">{fullAuditResult.seoAnalysis.headings.h2Count}</Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <span className="text-sm font-mono">H3</span>
                              <Badge variant="primary">{fullAuditResult.seoAnalysis.headings.h3Count}</Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <span className="text-sm font-mono">H4</span>
                              <Badge variant="primary">{fullAuditResult.seoAnalysis.headings.h4Count}</Badge>
                            </div>
                          </div>
                          {fullAuditResult.seoAnalysis.headings.h1.length > 0 && (
                            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <span className="text-sm font-medium text-green-700 dark:text-green-300">H1:</span>
                              {fullAuditResult.seoAnalysis.headings.h1.map((h1: string, i: number) => (
                                <p key={i} className="text-sm text-gray-600 dark:text-gray-400 mt-1">"{h1}"</p>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Content & Links */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Link2 className="w-4 h-4" />
                            Conteúdo & Links
                          </h4>
                          <div className="space-y-3">
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <div className="grid grid-cols-2 gap-4 text-center">
                                <div>
                                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {fullAuditResult.seoAnalysis.content.wordCount}
                                  </p>
                                  <p className="text-xs text-gray-500">Palavras</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {fullAuditResult.seoAnalysis.content.textToHtmlRatio}%
                                  </p>
                                  <p className="text-xs text-gray-500">Ratio Texto</p>
                                </div>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <div className="grid grid-cols-2 gap-4 text-center">
                                <div>
                                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {fullAuditResult.seoAnalysis.links.internalCount}
                                  </p>
                                  <p className="text-xs text-gray-500">Links Internos</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                    {fullAuditResult.seoAnalysis.links.externalCount}
                                  </p>
                                  <p className="text-xs text-gray-500">Links Externos</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Images */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            Imagens
                          </h4>
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
                              <div>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                  {fullAuditResult.seoAnalysis.images.total}
                                </p>
                                <p className="text-xs text-gray-500">Total</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                  {fullAuditResult.seoAnalysis.images.withAlt}
                                </p>
                                <p className="text-xs text-gray-500">Com Alt</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                  {fullAuditResult.seoAnalysis.images.withoutAlt}
                                </p>
                                <p className="text-xs text-gray-500">Sem Alt</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Technical Checks */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Verificações Técnicas
                          </h4>
                          <div className="space-y-2">
                            {[
                              { key: 'usesHttps', label: 'HTTPS', value: fullAuditResult.seoAnalysis.technical.usesHttps },
                              { key: 'hasViewport', label: 'Viewport Meta', value: fullAuditResult.seoAnalysis.technical.hasViewport },
                              { key: 'hasCharset', label: 'Charset', value: fullAuditResult.seoAnalysis.technical.hasCharset },
                              { key: 'hasLangAttribute', label: 'Lang Attribute', value: fullAuditResult.seoAnalysis.technical.hasLangAttribute },
                              { key: 'hasStructuredData', label: 'Structured Data', value: fullAuditResult.seoAnalysis.technical.hasStructuredData },
                              { key: 'hasFavicon', label: 'Favicon', value: fullAuditResult.seoAnalysis.technical.hasFavicon },
                            ].map(check => (
                              <div key={check.key} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                <span className="text-sm text-gray-700 dark:text-gray-300">{check.label}</span>
                                {check.value ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-500" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* SEO Issues */}
                        {fullAuditResult.seoAnalysis.issues && fullAuditResult.seoAnalysis.issues.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Problemas SEO ({fullAuditResult.seoAnalysis.issues.length})
                            </h4>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {fullAuditResult.seoAnalysis.issues.map((issue: any, idx: number) => (
                                <div
                                  key={idx}
                                  className={`p-2 rounded flex items-start gap-2 ${
                                    issue.type === 'error'
                                      ? 'bg-red-50 dark:bg-red-900/20'
                                      : issue.type === 'warning'
                                      ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                      : 'bg-blue-50 dark:bg-blue-900/20'
                                  }`}
                                >
                                  {issue.type === 'error' ? (
                                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                  ) : issue.type === 'warning' ? (
                                    <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                  )}
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{issue.message}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Load Time & Score */}
                      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-6 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Tempo de Carga: {fullAuditResult.seoAnalysis.loadTime}ms
                          </span>
                          <span>
                            Tamanho: {(fullAuditResult.seoAnalysis.contentLength / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Score SEO:</span>
                          <span className={`text-2xl font-bold ${getScoreColor(fullAuditResult.seoAnalysis.score)}`}>
                            {fullAuditResult.seoAnalysis.score}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Categories */}
                <div className="space-y-4">
                  {currentAuditData?.categories?.map((category) => (
                    <Card key={category.name}>
                      <div
                        className="px-3 sm:px-6 py-2 sm:py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        onClick={() => {
                          setExpandedCategories(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(category.name)) {
                              newSet.delete(category.name);
                            } else {
                              newSet.add(category.name);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                              {category.icon || <FileText className="w-5 h-5" />}
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-medium text-gray-900 dark:text-white">
                                {category.name}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {category.issues?.filter(i => i.severity !== 'passed').length || 0} {t('competitors.issues', 'problemas')} |{' '}
                                {category.issues?.filter(i => i.severity === 'passed').length || 0} {t('competitors.passed', 'passou')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                            <div className="text-right whitespace-nowrap">
                              <span className={`text-xl sm:text-2xl font-bold ${getScoreColor(category.score)}`}>
                                {category.score}
                              </span>
                              <span className="text-gray-400">/100</span>
                            </div>
                            {expandedCategories.has(category.name) ? (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {expandedCategories.has(category.name) && (
                        <CardContent className="pt-0 border-t border-gray-200 dark:border-gray-700">
                          <div className="space-y-4 mt-4">
                            {category.issues?.map((issue) => (
                              <div
                                key={issue.id}
                                className={`p-4 rounded-lg border ${
                                  issue.severity === 'critical'
                                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                                    : issue.severity === 'warning'
                                    ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                                    : issue.severity === 'info'
                                    ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                                    : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  {issue.severity === 'critical' && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                                  {issue.severity === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />}
                                  {issue.severity === 'info' && <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                                  {issue.severity === 'passed' && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                      <h4 className="font-medium text-gray-900 dark:text-white">
                                        {issue.title}
                                      </h4>
                                      <Badge variant={issue.severity === 'critical' ? 'danger' : issue.severity === 'warning' ? 'warning' : issue.severity === 'info' ? 'primary' : 'success'}>
                                        {issue.severity === 'critical' ? t('competitors.critical', 'Critico') :
                                         issue.severity === 'warning' ? t('competitors.warning', 'Aviso') :
                                         issue.severity === 'info' ? t('competitors.info', 'Info') : 'OK'}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                      {issue.description}
                                    </p>
                                    {issue.recommendation && (
                                      <p className="text-sm text-gray-700 dark:text-gray-300">
                                        <strong>{t('competitors.recommendation', 'Recomendacao')}:</strong> {issue.recommendation}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>

                {/* Export Button */}
                <div className="flex justify-end">
                  <Button variant="secondary" onClick={handleExportPdf} disabled={isExportingPdf}>
                    {isExportingPdf ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {t('competitors.exportPDF', 'Exportar PDF')}
                  </Button>
                </div>

                {/* Audit Info */}
                <Card>
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <span className="min-w-0">
                        URL: {' '}
                        <a
                          href={fullAuditResult.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                        >
                          {fullAuditResult.url}
                          <ExternalLink className="w-3 h-3 inline ml-1" />
                        </a>
                      </span>
                      <span>
                        {t('competitors.auditedAt', 'Auditado em')}: {' '}
                        {new Date(fullAuditResult.auditedAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Empty State */}
            {!fullAuditResult && !auditLoading && (
              <Card>
                <CardContent className="text-center py-16">
                  <FileSearch className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {t('competitors.auditEmptyTitle', 'Audite qualquer site')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    {t('competitors.auditEmptyDescription', 'Digite uma URL acima para executar uma auditoria SEO completa e identificar problemas tecnicos.')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

      </div>

      {/* Add Competitor Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('competitors.addCompetitor', 'Adicionar Concorrente')}
      >
        <div className="space-y-4">
          <Input
            label={t('competitors.modal.competitorName', 'Nome do Concorrente')}
            placeholder={t('competitors.modal.namePlaceholder', 'Ex: Acme Corp')}
            value={newCompetitor.name}
            onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
          />
          <Input
            label={t('competitors.modal.domain', 'Dominio')}
            placeholder={t('competitors.modal.domainPlaceholder', 'Ex: acme.com')}
            value={newCompetitor.domain}
            onChange={(e) => setNewCompetitor({ ...newCompetitor, domain: e.target.value })}
          />
          <Input
            label={t('competitors.modal.industry', 'Industria (opcional)')}
            placeholder={t('competitors.modal.industryPlaceholder', 'Ex: Marketing Digital')}
            value={newCompetitor.industry}
            onChange={(e) => setNewCompetitor({ ...newCompetitor, industry: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('competitors.modal.country', 'País')}
            </label>
            <select
              value={newCompetitor.country}
              onChange={(e) => setNewCompetitor({ ...newCompetitor, country: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            >
              {regions.length > 0 ? (
                regions.map((region) => (
                  <option key={region.code} value={region.code}>
                    {region.flag} {region.name}
                  </option>
                ))
              ) : (
                <>
                  <option value="BR">🇧🇷 Brasil</option>
                  <option value="US">🇺🇸 Estados Unidos</option>
                  <option value="PT">🇵🇹 Portugal</option>
                  <option value="ES">🇪🇸 Espanha</option>
                  <option value="MX">🇲🇽 México</option>
                  <option value="AR">🇦🇷 Argentina</option>
                  <option value="DE">🇩🇪 Alemanha</option>
                  <option value="FR">🇫🇷 França</option>
                  <option value="GB">🇬🇧 Reino Unido</option>
                  <option value="IT">🇮🇹 Itália</option>
                </>
              )}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleAddCompetitor} isLoading={isAdding}>
              {t('common.add', 'Adicionar')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Competitor Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingCompetitor(null); }}
        title={t('competitors.editCompetitor', 'Editar Concorrente')}
      >
        {editingCompetitor && (
          <div className="space-y-4">
            <Input
              label={t('competitors.modal.name', 'Nome')}
              value={editingCompetitor.name}
              onChange={(e) => setEditingCompetitor({ ...editingCompetitor, name: e.target.value })}
              placeholder={t('competitors.modal.namePlaceholder', 'Ex: Empresa ABC')}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('competitors.modal.domain', 'Domínio')}
              </label>
              <input
                type="text"
                value={editingCompetitor.domain}
                disabled
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">{t('competitors.modal.domainCannotChange', 'O domínio não pode ser alterado')}</p>
            </div>
            <Input
              label={t('competitors.modal.industry', 'Indústria (opcional)')}
              value={editingCompetitor.industry || ''}
              onChange={(e) => setEditingCompetitor({ ...editingCompetitor, industry: e.target.value })}
              placeholder={t('competitors.modal.industryPlaceholder', 'Ex: Tecnologia, Varejo, Saúde')}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('competitors.modal.country', 'País')}
              </label>
              <select
                value={editingCompetitor.country || 'BR'}
                onChange={(e) => setEditingCompetitor({ ...editingCompetitor, country: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              >
                {regions.length > 0 ? (
                  regions.map((region) => (
                    <option key={region.code} value={region.code}>
                      {region.flag} {region.name}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="BR">🇧🇷 Brasil</option>
                    <option value="US">🇺🇸 Estados Unidos</option>
                    <option value="PT">🇵🇹 Portugal</option>
                    <option value="ES">🇪🇸 Espanha</option>
                    <option value="MX">🇲🇽 México</option>
                    <option value="AR">🇦🇷 Argentina</option>
                    <option value="DE">🇩🇪 Alemanha</option>
                    <option value="FR">🇫🇷 França</option>
                    <option value="GB">🇬🇧 Reino Unido</option>
                    <option value="IT">🇮🇹 Itália</option>
                  </>
                )}
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => { setIsEditModalOpen(false); setEditingCompetitor(null); }}>
                {t('common.cancel', 'Cancelar')}
              </Button>
              <Button onClick={handleSaveEdit} isLoading={isSaving}>
                {t('common.save', 'Salvar')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompetitorsPage;
